// Core Imports
import React, { useState } from "react";
import PropTypes from "prop-types";

// Bootstrap Components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

// MUI Components
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";

// Custom Components
import GetInTouch from "../components/GetInTouch";

// Gallery Images
import Banner from "../assets/images/gallery/gallery-banner-s.jpeg"

import Digger1 from "../assets/images/gallery/sany-digger.jpg";
import Digger2 from "../assets/images/gallery/kobelco-140.jpg";
import Digger3 from "../assets/images/gallery/komatsu-pc200.jpg";
import Digger4 from "../assets/images/gallery/komatsu-pc130.jpg";

import Earthmoving1 from "../assets/images/gallery/grader-hyundai.jpg";
import Earthmoving2 from "../assets/images/gallery/roller-bomag.jpeg";
import Earthmoving3 from "../assets/images/gallery/wheel-loader-xcmg.jpeg";

import Forklift1 from "../assets/images/gallery/jcb-telehandler.jpeg";
import Forklift2 from "../assets/images/gallery/electric-forklift.jpeg";

import Concrete1 from "../assets/images/gallery/concrete-mixer.jpeg";
import Concrete2 from "../assets/images/gallery/concrete-pump.jpeg";

import Crush1 from "../assets/images/gallery/impact-crusher.jpeg";
import Crush2 from "../assets/images/gallery/screen-3-way.jpeg";

import Attachment1 from "../assets/images/gallery/digger-attachment.jpeg";

import Truck1 from "../assets/images/gallery/man-truck.jpg";
import Truck2 from "../assets/images/gallery/fuso-truck.jpg";
import Truck3 from "../assets/images/gallery/water-truck.jpg";
import Truck4 from "../assets/images/gallery/scania-truck.jpg";

// Modal styling for image preview
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  boxShadow: 24,
};

// Image collections for different tabs
const tabImagesAll = {
  "Sany 3.5 Tonner": Digger1, "Kobelco 140": Digger2, "Komatsu PC200": Digger3, "Komatsu PC130": Digger4,
  "GRADER Hyundai HG130-3 (2022)": Earthmoving1, "ROLLER Bomag BW213 D-5": Earthmoving2, "WHEEL LOADER XCMG 12 ton": Earthmoving3,
  "JCB Telehandler": Forklift1, "Electric forklift": Forklift2,
  "Concrete mixer truck": Concrete1, "Concrete pump": Concrete2,
  "Impact Crusher": Crush1, "Screen 3 way": Crush2,
  // "Various digger attachments": Attachment1,
  "MAN 3-axle tipper truck": Truck1, "FUSO 3-axle tipper truck": Truck2, "Scania tractor unit and B-train": Truck4,
}

const tabImagesExcavator = {
  "Sany 3.5 Tonner": Digger1, "Kobelco 140": Digger2, "Komatsu PC200": Digger3, "Komatsu PC130": Digger4,
}
const tabImagesEarthMoving = {
  "GRADER Hyundai HG130-3 (2022)": Earthmoving1, "ROLLER Bomag BW213 D-5": Earthmoving2, "WHEEL LOADER XCMG 12 ton": Earthmoving3,
}
const tabImagesTelehandler = {
  "JCB Telehandler": Forklift1, "Electric forklift": Forklift2,
}
const tabImagesConcrete = {
  "Concrete mixer truck": Concrete1, "Concrete pump": Concrete2,
}
const tabImagesCrush = {
  "Impact Crusher": Crush1, "Screen 3 way": Crush2,
}
// const tabImagesAttachment = {
//   "Various digger attachments": Attachment1,
// }
const tabImagesTruck = {
  "MAN 3-axle tipper truck": Truck1, "FUSO 3-axle tipper truck": Truck2, "Scania tractor unit and B-train": Truck4,
}


// Accessible tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// Accessibility props generator
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

// Main Gallery Component
const Gallery = () => {
  const [tabValue, setTabValue] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(null);

  // Handle tab switching
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Open modal with selected image
  const handleOpenModal = (image) => {
    setActiveImage(image);
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => setModalOpen(false);

  return (
    <div>
      {/* Breadcrumb Section */}
      <section id="breadcrumbs" className="breadcrumbs"
        style={{
          backgroundImage: `url(${Banner})`,
        }}
      >
        <div className="container">
          <h1>Our Machinery & Equipment</h1>
          <ul>
            <li><a href="./">Home</a></li>
            <li>Gallery</li>
          </ul>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="gallery">
        <Container>
          <Box>
            {/* Tab Navigation */}
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="gallery tabs"
              centered
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="All" {...a11yProps(0)} />
              <Tab label="Excavators / Diggers" {...a11yProps(1)} />
              <Tab label="Other earthmoving machinery" {...a11yProps(2)} />
              <Tab label="Telehandler / forklift" {...a11yProps(3)} />
              <Tab label="Concrete Equipment" {...a11yProps(4)} />
              <Tab label="Crushing & Screening" {...a11yProps(5)} />
              {/* <Tab label="Attachments & Accessories" {...a11yProps(6)} /> */}
              <Tab label="Trucks" {...a11yProps(6)} />
            </Tabs>

            {/* Tab Content */}
            <TabPanel value={tabValue} index={0}>
              <Row>
                {Object.entries(tabImagesAll).map(([name, image], idx) => (
                  <Col key={idx} md={4} className="mb-4 text-center">
                    <img
                      src={image}
                      alt={name}
                      className="img-fluid gallery-image"
                      onClick={() => handleOpenModal(image)}
                      style={{ cursor: "pointer" }}
                    />
                    <Typography variant="subtitle1" sx={{ mt: 1 }} className="img-text">
                      {name}
                    </Typography>
                  </Col>
                ))}
              </Row>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Row>
                {Object.entries(tabImagesExcavator).map(([name, image], idx) => (
                  <Col key={idx} md={4} className="mb-4 text-center">
                    <img
                      src={image}
                      alt={name}
                      className="img-fluid gallery-image"
                      onClick={() => handleOpenModal(image)}
                      style={{ cursor: "pointer" }}
                    />
                    <Typography variant="subtitle1" sx={{ mt: 1 }} className="img-text">
                      {name}
                    </Typography>
                  </Col>
                ))}
              </Row>
            </TabPanel>
            
            <TabPanel value={tabValue} index={2}>
              <Row>
                {Object.entries(tabImagesEarthMoving).map(([name, image], idx) => (
                  <Col key={idx} md={4} className="mb-4 text-center">
                    <img
                      src={image}
                      alt={name}
                      className="img-fluid gallery-image"
                      onClick={() => handleOpenModal(image)}
                      style={{ cursor: "pointer" }}
                    />
                    <Typography variant="subtitle1" sx={{ mt: 1 }} className="img-text">
                      {name}
                    </Typography>
                  </Col>
                ))}
              </Row>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Row>
                {Object.entries(tabImagesTelehandler).map(([name, image], idx) => (
                  <Col key={idx} md={4} className="mb-4 text-center">
                    <img
                      src={image}
                      alt={name}
                      className="img-fluid gallery-image"
                      onClick={() => handleOpenModal(image)}
                      style={{ cursor: "pointer" }}
                    />
                    <Typography variant="subtitle1" sx={{ mt: 1 }} className="img-text">
                      {name}
                    </Typography>
                  </Col>
                ))}
              </Row>
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              <Row>
                {Object.entries(tabImagesConcrete).map(([name, image], idx) => (
                  <Col key={idx} md={4} className="mb-4 text-center" >
                    <img
                      src={image}
                      alt={name}
                      className="img-fluid gallery-image"
                      onClick={() => handleOpenModal(image)}
                      style={{ cursor: "pointer" }}
                    />
                    <Typography variant="subtitle1" sx={{ mt: 1 }} className="img-text">
                      {name}
                    </Typography>
                  </Col>
                ))}
              </Row>
            </TabPanel>

            <TabPanel value={tabValue} index={5}>
              <Row>
                {Object.entries(tabImagesCrush).map(([name, image], idx) => (
                  <Col key={idx} md={4} className="mb-4 text-center">
                    <img
                      src={image}
                      alt={name}
                      className="img-fluid gallery-image"
                      onClick={() => handleOpenModal(image)}
                      style={{ cursor: "pointer" }}
                    />
                    <Typography variant="subtitle1" sx={{ mt: 1 }} className="img-text">
                      {name}
                    </Typography>
                  </Col>
                ))}
              </Row>
            </TabPanel>

            {/* <TabPanel value={tabValue} index={6}>
              <Row>
                {Object.entries(tabImagesAttachment).map(([name, image], idx) => (
                  <Col key={idx} md={4} className="mb-4 text-center">
                    <img
                      src={image}
                      alt={name}
                      className="img-fluid gallery-image"
                      onClick={() => handleOpenModal(image)}
                      style={{ cursor: "pointer" }}
                    />
                    <Typography variant="subtitle1" sx={{ mt: 1 }} className="img-text">
                      {name}
                    </Typography>
                  </Col>
                ))}
              </Row>
            </TabPanel> */}

            <TabPanel value={tabValue} index={6}>
              <Row>
                {Object.entries(tabImagesTruck).map(([name, image], idx) => (
                  <Col key={idx} md={4} className="mb-4 text-center">
                    <img
                      src={image}
                      alt={name}
                      className="img-fluid gallery-image"
                      onClick={() => handleOpenModal(image)}
                      style={{ cursor: "pointer" }}
                    />
                    <Typography variant="subtitle1" sx={{ mt: 1 }} className="img-text">
                      {name}
                    </Typography>
                  </Col>
                ))}
              </Row>
            </TabPanel>
          </Box>
        </Container>
      </section>

      {/* Modal for Image Preview */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="gallery-modal"
        aria-describedby="preview selected gallery image"
      >
        <Box sx={modalStyle}>
          <img
            src={activeImage}
            alt="SADP Gallery Preview"
            className="img-fluid"
          />
        </Box>
      </Modal>

      {/* Call-to-Action Section */}
      <GetInTouch />
    </div>
  );
};

export default Gallery;